import { useEffect, useState } from "react";

import { initKeycloak } from "./keycloak";
import { isPasswordGrantMode, restorePasswordSession } from "./password-grant";

/**
 * Hook de boot d'authentification — à monter une seule fois, au sommet de
 * l'app (`App.tsx`), AVANT le router : les gardes de routes lisent l'état
 * d'authentification, fiable seulement après l'init.
 *
 * - Mode **sso** : `keycloak.init` (check-sso, PKCE) restaure une session
 *   IdP existante.
 * - Mode **password** : pas d'init keycloak-js (le client confidentiel n'est
 *   pas supporté) — on restaure la session persistée via le refresh token.
 *
 * La promesse est un singleton de module : le double-mount de
 * React.StrictMode ne relance jamais l'init.
 */

type KeycloakInitStatus = "loading" | "ready" | "error";

let initPromise: Promise<boolean> | null = null;

function ensureKeycloakInit(): Promise<boolean> {
  if (!initPromise) {
    initPromise = isPasswordGrantMode()
      ? restorePasswordSession()
      : initKeycloak();
  }
  return initPromise;
}

export function useKeycloakInit(): { status: KeycloakInitStatus } {
  const [status, setStatus] = useState<KeycloakInitStatus>("loading");

  useEffect(() => {
    let mounted = true;
    ensureKeycloakInit()
      .then(() => {
        if (mounted) setStatus("ready");
      })
      .catch(() => {
        // Init impossible (Keycloak injoignable, config absente…) : l'écran
        // d'erreur avec bouton recharger est rendu par App.
        if (mounted) setStatus("error");
      });
    return () => {
      mounted = false;
    };
  }, []);

  return { status };
}
